import type { LocalMessage } from '@swchat/client-sdk';
import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent, type Ref } from 'react';
import { Icon } from '../design';
import { Bubble, type BubbleProps } from './Bubble';
import { dayFmt } from './format';

/** Cách đáy quá ngưỡng này (px) thì coi là đã cuộn lên xem tin cũ → hiện nút cuộn xuống. */
const AWAY_PX = 240;

/** Tin liền nhau cùng người gửi trong vòng 5 phút → gộp (ẩn avatar/tên). */
const GROUP_WINDOW_MS = 5 * 60_000;

export interface DropHandlers {
  onDragOver?: (e: DragEvent) => void;
  onDragLeave?: () => void;
  onDrop?: (e: DragEvent) => void;
}

/** Vùng tin nhắn: chia theo ngày, mỗi tin là một `Bubble`; kéo-thả file (C1) qua `drop`. */
export function MessageList({
  messages,
  bubble,
  dragOver,
  drop,
  bottomRef,
  onNearBottom,
}: {
  messages: LocalMessage[];
  /** Props của từng bong bóng (trừ `continued`, do danh sách tính). */
  bubble: (m: LocalMessage) => Omit<BubbleProps, 'continued'>;
  dragOver: boolean;
  drop: DropHandlers;
  bottomRef: Ref<HTMLDivElement>;
  /** Báo khi người dùng cuộn lên xa đáy / quay lại gần đáy (để cha không kéo xuống khi đang đọc tin cũ). */
  onNearBottom?: (near: boolean) => void;
}) {
  const scroller = useRef<HTMLDivElement>(null);
  const [away, setAway] = useState(false);
  const measure = useCallback(() => {
    const el = scroller.current;
    if (!el) return;
    const isAway = el.scrollHeight - el.scrollTop - el.clientHeight > AWAY_PX;
    setAway(isAway);
    onNearBottom?.(!isAway);
  }, [onNearBottom]);
  // Nội dung đổi (tin mới, tải thêm lịch sử) → đo lại.
  useEffect(measure, [measure, messages.length]);

  const grouped = useMemo(() => {
    const out: { day: string; items: LocalMessage[] }[] = [];
    for (const m of messages) {
      const day = dayFmt.format(m.ts);
      if (out.at(-1)?.day !== day) out.push({ day, items: [] });
      out.at(-1)!.items.push(m);
    }
    return out;
  }, [messages]);

  return (
    <div className="ws-messages-wrap">
    {/* Vùng cuộn nhận focus (Tab) để cuộn bằng phím — yêu cầu tiếp cận (W1.6, axe `scrollable-region-focusable`). */}
    <div
      ref={scroller}
      className={`ws-messages${dragOver ? ' drag-over' : ''}`}
      data-testid="messages"
      role="region"
      aria-label="Tin nhắn"
      tabIndex={0}
      onScroll={measure}
      {...drop}
    >
      {grouped.map((g) => (
        <div key={g.day} className="ws-day-group">
          <div className="ws-day">
            <span>{g.day}</span>
          </div>
          {g.items.map((m, i) => {
            const prev = g.items[i - 1];
            const continued =
              !!prev && prev.sender_user_id === m.sender_user_id && !prev.deleted && m.ts - prev.ts < GROUP_WINDOW_MS && !m.forwarded_from;
            return <Bubble key={m.id} {...bubble(m)} continued={continued} />;
          })}
        </div>
      ))}
      <div ref={bottomRef} />
    </div>
    {away && (
      <button
        type="button"
        className="ws-jump-bottom"
        aria-label="Cuộn xuống tin mới nhất"
        title="Cuộn xuống tin mới nhất"
        data-testid="jump-bottom"
        onClick={() => scroller.current?.scrollTo({ top: scroller.current.scrollHeight, behavior: 'smooth' })}
      >
        <Icon name="down" size={20} />
      </button>
    )}
    </div>
  );
}
